import { useState } from 'react';
import type { FavoriteSite } from '../types';
import type { AuthUser } from '../hooks/useAuth';

interface Props {
  favorites: FavoriteSite[];
  onAdd: (name: string, template: string) => void;
  onUpdate: (id: string, patch: Partial<FavoriteSite>) => void;
  onDelete: (id: string) => void;
  onResetDefaults: () => void;
  onClose: () => void;
  // Auth state drives the UD MCP key section: anonymous + Guest see a
  // gated message, paid tiers see the input. onUdKeyChange is called
  // after a successful save or remove so App.tsx can refresh the
  // cached AuthUser snapshot.
  user: AuthUser | null;
  isPaid: boolean;
  onUdKeyChange: () => void;
}

function readCookie(name: string): string | null {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

async function callAuth(
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<any> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (method !== 'GET') {
    const csrf = readCookie('lites_csrf');
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }
  const res = await fetch('/api/auth' + path, {
    method,
    credentials: 'same-origin',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(json?.error?.message || `HTTP ${res.status}`);
  return json;
}

export function Settings({
  favorites,
  onAdd,
  onUpdate,
  onDelete,
  onResetDefaults,
  onClose,
  user,
  isPaid,
  onUdKeyChange,
}: Props) {
  const [newName, setNewName] = useState('');
  const [newTemplate, setNewTemplate] = useState('');
  const [udKeyInput, setUdKeyInput] = useState('');
  const [udKeyBusy, setUdKeyBusy] = useState<'saving' | 'removing' | null>(null);
  const [udKeyError, setUdKeyError] = useState<string | null>(null);
  const udKeySet = Boolean(user?.udMcpApiKeyLast4);
  const udKeyLast4 = user?.udMcpApiKeyLast4 || '';

  const handleAdd = () => {
    if (!newName.trim() || !newTemplate.trim()) return;
    onAdd(newName, newTemplate);
    setNewName('');
    setNewTemplate('');
  };

  async function saveUdKey() {
    const apiKey = udKeyInput.trim();
    if (apiKey.length < 16) {
      setUdKeyError('UD MCP key looks too short.');
      return;
    }
    setUdKeyError(null);
    setUdKeyBusy('saving');
    try {
      await callAuth('POST', '/ud-key', { apiKey });
      setUdKeyInput('');
      onUdKeyChange();
    } catch (e: any) {
      setUdKeyError(e?.message || 'Save failed.');
    } finally {
      setUdKeyBusy(null);
    }
  }

  async function removeUdKey() {
    if (!window.confirm('Remove your stored Unstoppable Domains MCP key?')) return;
    setUdKeyError(null);
    setUdKeyBusy('removing');
    try {
      await callAuth('DELETE', '/ud-key');
      onUdKeyChange();
    } catch (e: any) {
      setUdKeyError(e?.message || 'Remove failed.');
    } finally {
      setUdKeyBusy(null);
    }
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Settings">
      <div className="modal__overlay" onClick={onClose} />
      <div className="modal__panel">
        <div className="modal__header">
          <h2>Favorite sites</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close settings"
          >
            ×
          </button>
        </div>

        <p className="modal__hint">
          Templates use <code>{'{keyword}'}</code> for the selected option
          (literal text, URL-encoded). Use <code>{'{domain}'}</code> for
          domain registrars: it strips spaces, lowercases the value, and
          appends <code>.com</code> when no TLD is present, so
          &ldquo;Red Kite&rdquo; becomes &ldquo;redkite.com&rdquo;.
        </p>

        <ul className="favorites">
          {favorites.map((f) => (
            <li key={f.id} className="favorite">
              <input
                className="favorite__name"
                value={f.name}
                onChange={(e) => onUpdate(f.id, { name: e.target.value })}
                placeholder="Name"
              />
              <input
                className="favorite__template"
                value={f.template}
                onChange={(e) => onUpdate(f.id, { template: e.target.value })}
                placeholder="https://example.com/search?q={domain}"
              />
              <button
                type="button"
                className="button button--ghost"
                onClick={() => onDelete(f.id)}
                aria-label={`Delete ${f.name}`}
              >
                Delete
              </button>
            </li>
          ))}
        </ul>

        <div className="favorite favorite--new">
          <input
            className="favorite__name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New site name"
          />
          <input
            className="favorite__template"
            value={newTemplate}
            onChange={(e) => setNewTemplate(e.target.value)}
            placeholder="https://example.com/search?q={domain}"
          />
          <button
            type="button"
            className="button button--primary"
            onClick={handleAdd}
            disabled={!newName.trim() || !newTemplate.trim()}
          >
            Add
          </button>
        </div>

        <hr className="settings__divider" />

        <section className="settings__section">
          <h3 className="settings__heading">Unstoppable Domains MCP key</h3>
          <p className="settings__hint">
            Optional power feature. With a key, Lites checks all 10 generated
            names in one call to Unstoppable Domains and shows clickable{' '}
            <strong>Register</strong> / <strong>Aftermarket</strong> /{' '}
            <strong>Broker</strong> / <strong>Backorder</strong> pills inline
            so you can act on each one in a click. Without a key, the{' '}
            <strong>Bulk check</strong> button just opens UD&rsquo;s public
            bulk search page in a new tab. Sign in to Unstoppable Domains in
            your browser with the same account this key belongs to so
            cart-add works seamlessly. The key is encrypted at rest and never
            leaves the server. Get a key at{' '}
            <a
              href="https://unstoppabledomains.com/manage?page=user-profile&overlay=settings"
              target="_blank"
              rel="noopener noreferrer"
            >
              Unstoppable Domains -&gt; Account Settings
            </a>
            .
          </p>

          {!user && (
            <p className="settings__notice">
              Sign in to add an Unstoppable Domains MCP key.
            </p>
          )}

          {user && !isPaid && (
            <p className="settings__notice">
              Adding a key for inline availability pills is a Subscriber
              feature. The Bulk check button still works without one — it
              opens UD&rsquo;s public bulk search page in a new tab.
            </p>
          )}

          {user && isPaid && udKeySet && (
            <div className="ud-key__row">
              <span className="ud-key__masked" aria-label="Saved UD MCP key">
                ••••<strong>{udKeyLast4}</strong>
              </span>
              <button
                type="button"
                className="button button--ghost button--small"
                onClick={() => void removeUdKey()}
                disabled={udKeyBusy !== null}
              >
                {udKeyBusy === 'removing' ? 'Removing…' : 'Remove'}
              </button>
            </div>
          )}

          {user && isPaid && !udKeySet && (
            <div className="ud-key__row">
              <input
                type="password"
                className="ud-key__input"
                value={udKeyInput}
                onChange={(e) => setUdKeyInput(e.target.value)}
                placeholder="Paste your UD MCP key"
                autoComplete="off"
                spellCheck={false}
              />
              <button
                type="button"
                className="button button--primary button--small"
                onClick={() => void saveUdKey()}
                disabled={udKeyBusy !== null || udKeyInput.trim().length < 16}
              >
                {udKeyBusy === 'saving' ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}

          {udKeyError && <p className="modal__error">{udKeyError}</p>}
        </section>

        <div className="modal__footer">
          <button type="button" className="button button--ghost" onClick={onResetDefaults}>
            Reset to defaults
          </button>
          <button type="button" className="button button--primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
